import {
  PageHeaderWrapper,
  Text,
  Title,
  type useModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { getGradientByTimeSlot } from "@src/appV2/redesign/Shift/getGradientByTimeSlot";
import { getSafeAreaPaddings } from "@src/appV2/redesign/utils/getSafeAreaPaddings";
import { isAtLeast12HoursAway } from "@src/appV2/redesign/utils/isAtLeast12HoursAway";
import { type ShiftStateData } from "@src/appV2/Shifts/Shift/ShiftState/types";
import {
  type ShiftPeriod,
  shiftPeriodToTimeSlot,
  type WorkerShift,
} from "@src/appV2/Shifts/Shift/types";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { format, parseISO, startOfMinute } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { formatInTimeZone } from "date-fns-tz";
import { useHistory } from "react-router-dom";

import { IconButton } from "../../../components/IconButton";
import { formatTime } from "../../../utils/formatTime";
import { MY_SHIFTS_FULL_PATH } from "../../path";
import { useShiftOptionsActions } from "../hooks/useShiftActionVisibility";
import { hasShiftStarted } from "../utils/hasShiftStarted";
import { ShiftOptionsBottomSheet } from "./ShiftOptionsBottomSheet";

export interface ShiftDetailsHeaderWrapperProps {
  onBackClick?: () => void;
  shift: WorkerShift;
  shiftState: ShiftStateData;
  isTestAccount: boolean;
  shiftStartDate: Date;
  isPageDataSuccess: boolean;
  workplaceTimeZone?: string;
  shiftAgentReq: string;
  shiftOptionsModalState: ReturnType<typeof useModalState>;
  isBadgeVerificationEnabled: boolean;
  onOpenShiftOptions: () => void;
  onCloseShiftOptions: () => void;
  onClockIn: () => void;
  onUpdateETA: () => void;
  onCancelShift: () => void;
  onTransferShift: () => void;
  onUploadTimesheet?: () => void;
  onAcceptNonInstantPay: () => void;
}

export function ShiftDetailsHeaderWrapper(props: ShiftDetailsHeaderWrapperProps) {
  const {
    isTestAccount,
    onBackClick,
    shift,
    shiftState,
    shiftStartDate,
    isPageDataSuccess,
    workplaceTimeZone,
    shiftAgentReq,
    shiftOptionsModalState,
    isBadgeVerificationEnabled,
    onOpenShiftOptions,
    onCloseShiftOptions,
    onClockIn,
    onUpdateETA,
    onCancelShift,
    onTransferShift,
    onUploadTimesheet,
    onAcceptNonInstantPay,
  } = props;
  const history = useHistory();
  const currentDate = useCurrentDate(1000);
  const { qualificationNameToDisplayLabelMap } = useGetQualificationDisplayLabels([shiftAgentReq]);

  const qualificationDisplayLabel =
    qualificationNameToDisplayLabelMap.get(shiftAgentReq) ?? shiftAgentReq;

  function handleBackClick() {
    logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_BACK_BUTTON_TAPPED, {
      shiftId: shift._id,
      sourceUI: "header",
    });
    if (onBackClick) {
      onBackClick();
    } else {
      history.push(MY_SHIFTS_FULL_PATH);
    }
  }

  const now = startOfMinute(currentDate);
  const shiftStarted = hasShiftStarted({ shiftStartDate, currentDate: now });
  const isInstantPayShiftThatHasStarted = shift.isInstantPay && shiftStarted;

  const { hasVisibleOptions } = useShiftOptionsActions({
    shift,
    shiftState,
    currentDate: now,
    isTestAccount,
  });

  const isShiftMoreThan12HoursAway = isAtLeast12HoursAway(shiftStartDate, now);

  function getHeaderTitle(): string {
    if (!isPageDataSuccess) {
      return "";
    }

    if (isShiftMoreThan12HoursAway) {
      // For shifts more than 12 hours away, show the full date
      const moreThanTwelveHourDateFormat = "EEEE, MMM d";
      return workplaceTimeZone
        ? formatInTimeZone(shiftStartDate, workplaceTimeZone, moreThanTwelveHourDateFormat)
        : format(shiftStartDate, moreThanTwelveHourDateFormat);
    }

    // For shifts within 12 hours, explicitly handle all cases
    const targetArrivalTime = shift.computedArrival?.targetArrivalTime;
    const hasTargetArrival = isInstantPayShiftThatHasStarted && isDefined(targetArrivalTime);

    if (hasTargetArrival) {
      // Instant pay shift that has started with target arrival time
      const formattedTime = formatTime(parseISO(targetArrivalTime), workplaceTimeZone);
      return `Clock in to your ${qualificationDisplayLabel} shift by ${formattedTime}`;
    }

    if (shift.isInstantPay && shiftStarted && !isDefined(targetArrivalTime)) {
      // Instant pay shift that has started but no target arrival time
      const formattedTime = formatTime(shiftStartDate, workplaceTimeZone);
      return `Clock in to your ${qualificationDisplayLabel} shift at ${formattedTime}`;
    }

    if (shift.isInstantPay) {
      // Instant pay shift that hasn't started yet
      const formattedTime = formatTime(shiftStartDate, workplaceTimeZone);
      return `Prepare for your ${qualificationDisplayLabel} shift at ${formattedTime}`;
    }

    if (shiftStarted) {
      // Regular shift that has started
      const formattedTime = formatTime(shiftStartDate, workplaceTimeZone);
      return `Start your ${qualificationDisplayLabel} shift at ${formattedTime}`;
    }

    // Regular shift that hasn't started yet
    const formattedTime = formatTime(shiftStartDate, workplaceTimeZone);
    return `Prepare for your ${qualificationDisplayLabel} shift at ${formattedTime}`;
  }

  const headerSubtitle =
    isPageDataSuccess && isShiftMoreThan12HoursAway ? qualificationDisplayLabel : undefined;

  return (
    /**
     * We are setting paddingTop to 0 because we want our gradient background to go all the way to the top of the page and behind the iOS status menu.
     * We can't pass in the background to the header because it is a gradient and we need it to cover both the header part and our title part.
     * Having two gradient backgrounds on two stacked elements creates an uneven color and a line of separation.
     */
    // TODO: Replace with StickyScrollPageHeader / PageHeaderTopRow
    <>
      <PageHeaderWrapper sx={{ paddingTop: 0 }}>
        <Stack
          sx={(theme) => ({
            ...getSafeAreaPaddings({ top: true, fallbackTop: theme.spacing(3) }),
            paddingBottom: 7,
            paddingX: 6,
            transition: theme.transitions.create(["background"]),
            background: isDefined(shift.name)
              ? getGradientByTimeSlot({
                  timeSlot: shiftPeriodToTimeSlot[shift.name as ShiftPeriod],
                  theme,
                })
              : undefined,
          })}
        >
          <Stack spacing={6}>
            <Stack flexDirection="row" justifyContent="space-between">
              <IconButton
                iconType="chevron-left"
                variant="outlined"
                aria-label="Go back"
                onClick={() => {
                  handleBackClick();
                }}
              />
              {hasVisibleOptions && (
                <IconButton
                  iconType="ellipsis"
                  variant="outlined"
                  aria-label="Options"
                  onClick={() => {
                    logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_OPTIONS_BUTTON_TAPPED, {
                      shiftId: shift._id,
                      sourceUI: "header",
                    });
                    onOpenShiftOptions();
                  }}
                />
              )}
            </Stack>
            <Stack spacing={4}>
              {isDefined(headerSubtitle) ? (
                <Text semibold variant="body1">
                  {headerSubtitle}
                </Text>
              ) : null}
              <Title component="h3" variant="h3">
                {getHeaderTitle()}
              </Title>
            </Stack>
          </Stack>
        </Stack>
      </PageHeaderWrapper>
      <ShiftOptionsBottomSheet
        modalState={shiftOptionsModalState}
        isTestAccount={isTestAccount}
        shift={shift}
        shiftState={shiftState}
        workplaceId={shift.facilityId}
        currentDate={now}
        isBadgeVerificationEnabled={isBadgeVerificationEnabled}
        onCancelShift={onCancelShift}
        onClockIn={onClockIn}
        onUpdateETA={onUpdateETA}
        onTransferShift={onTransferShift}
        onUploadTimesheet={onUploadTimesheet}
        onAcceptNonInstantPay={onAcceptNonInstantPay}
        onClose={onCloseShiftOptions}
      />
    </>
  );
}
